/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/components/ToolPanel/AddColumn
 * @desc Wizard to add a column
 */

import { EntitySearchMulti } from 'labstep-web/components/Entity/Search/Multi';
import { ICONS } from 'labstep-web/constants/icons';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { Flex } from 'labstep-web/core/Flex';
import { Header, SectionHeader } from 'labstep-web/core/Header';
import { useToolPanelContext } from 'labstep-web/grid/Index/components/ToolPanel/context';
import { EntityDataGridToolPanelListItem } from 'labstep-web/grid/Report/components/ToolPanel/ListItem';
import { getMetadataColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/metadata';
import { getProtocolValueAmountColumnDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/protocol-value/amount';
import { getProtocolValueResourceColumnDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/protocol-value/resource';
import { getProtocolValueResourceItemColumnDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/protocol-value/resourceItem';
import { getProtocolValueResourceLocationColumnDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/protocol-value/resourceLocation';
import { getProtocolValueResourceItemStatusColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/protocol-value/status';
import GridSmartTableTemplateLoader from 'labstep-web/grid/SmartTableProtocolValue/components/TemplateLoader';
import { ToolPanelState } from 'labstep-web/grid/SmartTableProtocolValue/components/ToolPanel';
import { GridToolBarService } from 'labstep-web/grid/services/grid-toolbar.service';
import type { IndexTemplatableEntity } from 'labstep-web/models';
import { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';
import { Resource } from 'labstep-web/models/resource.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import type React from 'react';
import type { IEntityDataGridToolPanelAddColumnProps } from './types';

export const EntityDataGridToolPanelAddColumn: React.FC<
  IEntityDataGridToolPanelAddColumnProps
> = ({
  gridSmartTableProtocolValueService,
  colIds,
  entityView,
  selectMetadata,
  experimentOrProtocol,
}) => {
  const toolPanelContext = useToolPanelContext();

  const protocolValueAmountColDef = getProtocolValueAmountColumnDef(
    experimentOrProtocol,
  );

  const protocolValueResourceNameColDef =
    getProtocolValueResourceColumnDef(experimentOrProtocol);

  const protocolValueResourceItemNameColDef =
    getProtocolValueResourceItemColumnDef(experimentOrProtocol);

  const protocolValueResourceLocationPathColumnDef =
    getProtocolValueResourceLocationColumnDef(experimentOrProtocol);

  const protocolValueResourceItemStatusColDef =
    getProtocolValueResourceItemStatusColDef(experimentOrProtocol);

  return (
    <GridSmartTableTemplateLoader
      entityName={entityView.entity_name}
      templateId={entityView.templateId}
    >
      {({ template }: { template?: IndexTemplatableEntity }) => {
        return (
          <div data-cy="grid/SmartTableProtocolValue/components/ToolPanel/AddColumn">
            <Flex vAlignContent="center" spacing="gap" shrink={0}>
              <ActionComponent
                type="icon"
                icon="arrow left"
                text="Go Back"
                onClick={() =>
                  toolPanelContext?.setToolPanelState(
                    ToolPanelState.default,
                  )
                }
              />
              <Header size="tiny" noMargin>
                Add Column
              </Header>
            </Flex>
            <small>Select which columns to show in the table.</small>
            <EntitySearchMulti
              staticLists={(searchQuery) => (
                <>
                  <SectionHeader label="System Fields" />
                  <Flex column key="static">
                    {GridToolBarService.showListItem(
                      'amount',
                      searchQuery,
                    ) && (
                      <EntityDataGridToolPanelListItem
                        icon={ICONS.protocol_value.info.amount_used}
                        actionIcon="eye"
                        text="Amount"
                        onClick={() => {
                          gridSmartTableProtocolValueService.addColumnDef(
                            protocolValueAmountColDef,
                          );
                        }}
                        disabled={
                          protocolValueAmountColDef.colId
                            ? colIds.includes(
                                protocolValueAmountColDef.colId,
                              )
                            : false
                        }
                      />
                    )}
                    {GridToolBarService.showListItem(
                      'resource',
                      searchQuery,
                    ) && (
                      <EntityDataGridToolPanelListItem
                        icon={ICONS.resource.primary}
                        actionIcon="eye"
                        text="Resource"
                        onClick={() => {
                          gridSmartTableProtocolValueService.addColumnDef(
                            protocolValueResourceNameColDef,
                          );
                        }}
                        disabled={
                          protocolValueResourceNameColDef.colId
                            ? colIds.includes(
                                protocolValueResourceNameColDef.colId,
                              )
                            : false
                        }
                      />
                    )}
                    {GridToolBarService.showListItem(
                      'item',
                      searchQuery,
                    ) && (
                      <EntityDataGridToolPanelListItem
                        icon={ICONS.resource_item.primary}
                        actionIcon="eye"
                        text="Item"
                        onClick={() => {
                          gridSmartTableProtocolValueService.addColumnDef(
                            protocolValueResourceItemNameColDef,
                          );
                        }}
                        disabled={
                          protocolValueResourceItemNameColDef.colId
                            ? colIds.includes(
                                protocolValueResourceItemNameColDef.colId,
                              )
                            : false
                        }
                      />
                    )}
                    {GridToolBarService.showListItem(
                      'location',
                      searchQuery,
                    ) && (
                      <EntityDataGridToolPanelListItem
                        icon={ICONS.resource_location.primary}
                        actionIcon="eye"
                        text="Location"
                        onClick={() => {
                          gridSmartTableProtocolValueService.addColumnDef(
                            protocolValueResourceLocationPathColumnDef,
                          );
                        }}
                        disabled={
                          protocolValueResourceLocationPathColumnDef.colId
                            ? colIds.includes(
                                protocolValueResourceLocationPathColumnDef.colId,
                              )
                            : false
                        }
                      />
                    )}
                    {GridToolBarService.showListItem(
                      'status',
                      searchQuery,
                    ) && (
                      <EntityDataGridToolPanelListItem
                        icon={'info circle'}
                        actionIcon="eye"
                        text="Status"
                        onClick={() => {
                          gridSmartTableProtocolValueService.addColumnDef(
                            protocolValueResourceItemStatusColDef,
                          );
                        }}
                        disabled={
                          protocolValueResourceItemStatusColDef.colId
                            ? colIds.includes(
                                protocolValueResourceItemStatusColDef.colId,
                              )
                            : false
                        }
                      />
                    )}
                  </Flex>
                </>
              )}
              lists={[
                {
                  key: Resource.entityName,
                  entityName: Metadata.entityName,
                  searchField: 'label_like',
                  params: {
                    permission_class: Resource.entityName,
                    group_id: entityView.group.id,
                    group_by_label: 1,
                    sort: 'label',
                    count: 8,
                    serializerGroups:
                      'protocol_condition_reporting,paginator',
                  },
                  header: (
                    <SectionHeader label="Resource Metadata Fields" />
                  ),
                  listItem: (entity: object) => {
                    const metadata = entity as Metadata;

                    const colDef =
                      metadata.type !== MetadataType.molecule &&
                      getMetadataColDef(
                        experimentOrProtocol,
                        Resource.entityName,
                        (entity as Metadata).type,
                        (entity as Metadata).label || '',
                      );
                    return (
                      <EntityDataGridToolPanelListItem
                        icon={
                          ICONS.metadata.type[
                            metadata.type as keyof typeof ICONS.metadata.type
                          ] || 'database'
                        }
                        actionIcon={
                          metadata.type === MetadataType.molecule
                            ? 'arrow right'
                            : 'eye'
                        }
                        text={metadata.label || 'Untitled data field'}
                        onClick={() => {
                          if (colDef) {
                            gridSmartTableProtocolValueService.addColumnDef(
                              colDef,
                            );
                          } else {
                            selectMetadata(
                              metadata,
                              Resource.entityName,
                            );

                            toolPanelContext?.setToolPanelState(
                              ToolPanelState.metadata_molecule,
                            );
                          }
                        }}
                        disabled={
                          colDef && colIds.includes(colDef.colId!)
                        }
                      />
                    );
                  },
                },
                {
                  key: ResourceItem.entityName,
                  entityName: Metadata.entityName,
                  searchField: 'label_like',
                  params: {
                    permission_class: ResourceItem.entityName,
                    group_id: entityView.group.id,
                    group_by_label: 1,
                    sort: 'label',
                    count: 8,
                    serializerGroups:
                      'protocol_condition_reporting,paginator',
                  },
                  header: (
                    <SectionHeader label="Item Metadata Fields" />
                  ),

                  listItem: (entity: object) => {
                    const metadata = entity as Metadata;

                    const colDef =
                      metadata.type !== MetadataType.molecule &&
                      getMetadataColDef(
                        experimentOrProtocol,
                        ResourceItem.entityName,
                        metadata.type,
                        metadata.label || '',
                      );
                    return (
                      <EntityDataGridToolPanelListItem
                        icon={
                          ICONS.metadata.type[
                            metadata.type as keyof typeof ICONS.metadata.type
                          ] || 'database'
                        }
                        actionIcon={
                          metadata.type === MetadataType.molecule
                            ? 'arrow right'
                            : 'eye'
                        }
                        text={metadata.label || 'Untitled data field'}
                        onClick={() => {
                          if (colDef) {
                            gridSmartTableProtocolValueService.addColumnDef(
                              colDef,
                            );
                          } else {
                            selectMetadata(
                              metadata,
                              ResourceItem.entityName,
                            );

                            toolPanelContext?.setToolPanelState(
                              ToolPanelState.metadata_molecule,
                            );
                          }
                        }}
                        disabled={
                          colDef && colIds.includes(colDef.colId!)
                        }
                      />
                    );
                  },
                },
              ]}
            />
            <ActionComponent
              icon="plus"
              type="text"
              text="Add Inventory Field"
              onClick={() => {
                if (toolPanelContext) {
                  toolPanelContext.setToolPanelState(
                    ToolPanelState.protocol_value,
                  );
                }
              }}
            />
          </div>
        );
      }}
    </GridSmartTableTemplateLoader>
  );
};
